import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ExternalLinkIcon, SparklesIcon } from 'lucide-react';
import type { JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useCurrentWorkspaceRoleState } from '@/features/workspace/useCurrentWorkspaceRole.js';
import { deriveCan } from '@/features/workspace/workspace-access.js';
import {
  chooseConnectorOwner,
  clearConnectorOwner,
} from '@/lib/api/quality-setup.js';
import {
  workspaceMembersQueryOptions,
  type WorkspaceRole,
} from '@/lib/api/workspaces.js';
import { ConnectorConnectFlow } from '@/integration/components/connectors/ConnectorConnectFlow.js';
import { ConnectorIcon } from '@/integration/components/connectors/ConnectorIcon.js';
import { ConnectorScopeProvider } from '@/integration/components/connectors/connector-scope.js';
import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import { agentGuideEntryUrl, qualityGuideUrl } from '@/lib/guide-links.js';
import { notify } from '@/lib/notify.js';

import { CopyRequestButton } from '../setup/CopyRequestButton.js';
import { openSetupInAgent } from '../setup/openSetupInAgent.js';

import {
  QUALITY_CONNECTOR_NAMES,
  useConnectorMeta,
  type ConnectorBadgeStatus,
  type QualityConnectorId,
} from './connector-status.js';

/** What Quality does with each connector, in one sentence for the modal. */
export const QUALITY_CONNECTOR_PURPOSE: Record<QualityConnectorId, string> = {
  jira: 'Quality reads Jira issues and their acceptance criteria to generate cases, and suggests story links for them.',
  confluence:
    'Quality reads Confluence pages as context when it generates cases.',
  testrail:
    'Quality reads suites and cases from TestRail to run them, and can publish new cases there.',
  zephyr:
    'Quality reads test cases from Zephyr Scale to run them, and can publish new cases there.',
  xray: 'Quality reads tests from Xray to run them, and can publish new cases there.',
  azure_devops:
    'Quality uses Azure DevOps to read Boards work items for generation, to run cases from Test Plans, and to publish new cases there.',
  github:
    "Quality reads the repos you pick to learn the app's screens and flows.",
  gitlab:
    "Quality reads the projects you pick to learn the app's screens and flows.",
  browserstack:
    'Quality runs mobile cases on BrowserStack devices and browsers.',
  reportportal: 'Quality publishes run results to Report Portal.',
};

/**
 * Each connector's "what Quality needs" entry in the CT Agent guide, by its entry id. A connector
 * with no entry falls back to the connectors step of the first-run setup guide.
 */
const CONNECTOR_GUIDE_ENTRIES: Partial<Record<QualityConnectorId, string>> = {
  jira: 'connect-jira',
  confluence: 'connect-confluence',
  testrail: 'connect-testrail',
  zephyr: 'connect-zephyr-scale',
  xray: 'connect-xray',
  azure_devops: 'connect-azure-devops',
  github: 'connect-github',
  gitlab: 'connect-gitlab',
  browserstack: 'connect-browserstack',
  reportportal: 'connect-report-portal',
};

/** Where "What Quality needs" leads for one connector. */
export function connectorGuideUrl(provider: QualityConnectorId): string {
  const entry = CONNECTOR_GUIDE_ENTRIES[provider];
  return entry
    ? agentGuideEntryUrl(entry)
    : qualityGuideUrl('digital-qa-first-run-setup', 'connectors');
}

const pillClass = {
  connected: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  disconnected: 'bg-muted text-muted-foreground',
  error: 'bg-red-500/10 text-red-700 dark:text-red-400',
  unavailable: 'bg-muted text-muted-foreground',
} as const;

const pillLabel = {
  connected: 'Connected',
  disconnected: 'Not connected',
  error: 'Needs reconnect',
  unavailable: 'Status unavailable',
} as const;

/** The sentence that says which store a working connector came from. */
function sourceLine(status: ConnectorBadgeStatus): string | null {
  if (status.source === 'project')
    return "Connected through this Quality project's own account.";
  if (status.source === 'identity')
    return 'Connected through the workspace connector.';
  return null;
}

/** The message a member without rights can send to an owner or editor. */
function adminRequest(name: string): string {
  return `Could you connect ${name} in our workspace (Settings → Connectors)? Quality needs it: ${window.location.origin}/quality`;
}

/**
 * Whether the caller may connect here. It fails closed: `checking` while the role loads offers no
 * action, and a role read that failed is treated as no rights, so the admin request is offered.
 */
function useConnectAccess(
  workspaceId: string | null,
  open: boolean,
): 'checking' | 'allowed' | 'denied' {
  const roleState = useCurrentWorkspaceRoleState(
    workspaceId ?? '',
    open && Boolean(workspaceId),
  );
  if (roleState.failed) return 'denied';
  if (!roleState.ready) return 'checking';
  return deriveCan(roleState.role as WorkspaceRole | null).manageConnectors
    ? 'allowed'
    : 'denied';
}

/**
 * Where a working connection is managed. A connection that comes from the Quality project's own
 * account lives in the workspace's Quality settings, not in its Connectors tab.
 */
function ManageConnectionLink({
  workspaceId,
  source,
}: {
  workspaceId: string;
  source: ConnectorBadgeStatus['source'];
}): JSX.Element {
  const inQualitySettings = source === 'project';
  return (
    <Link
      to="/workspaces/$id/manage"
      params={{ id: workspaceId }}
      search={{ tab: inQualitySettings ? 'quality' : 'connectors' }}
      className="text-xs font-medium text-primary hover:underline"
    >
      {inQualitySettings ? 'Manage in Quality settings' : 'Manage connection'}
    </Link>
  );
}

/**
 * Connect / reconnect, plus the hand-off to the agent. A reconnect starts its access dialog from what the
 * dead connection allows, read off the same presence that named it.
 */
function ConnectActions({
  provider,
  reconnect,
  reconnectConnectionId,
  workspaceId,
  explainUnavailableActions = false,
}: {
  provider: QualityConnectorId;
  reconnect: boolean;
  reconnectConnectionId: string | null;
  workspaceId: string | null;
  explainUnavailableActions?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const meta = useConnectorMeta(provider);
  const name = QUALITY_CONNECTOR_NAMES[provider];
  const reconnecting = useConnectorPresence(workspaceId).data?.providers[
    provider
  ]?.connections.find(
    (connection) => connection.connectionId === reconnectConnectionId,
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ConnectorScopeProvider value={{ workspaceId, canManage: true }}>
        {meta ? (
          <ConnectorConnectFlow
            explainUnavailableActions={explainUnavailableActions}
            meta={meta}
            connectionId={reconnectConnectionId}
            permissionLevel={reconnecting?.permissionLevel}
            allowDelete={reconnecting?.allowDelete}
            label={reconnect ? `Reconnect ${name}` : `Connect ${name}`}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          />
        ) : null}
      </ConnectorScopeProvider>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="gap-1 text-xs"
        onClick={() =>
          openSetupInAgent({
            entry: 'badge',
            navigate,
            workspaceId: workspaceId ?? undefined,
            request: reconnect
              ? `Help me fix the ${name} connection for Quality.`
              : `Help me connect ${name} for Quality.`,
          })
        }
      >
        <SparklesIcon className="size-3" aria-hidden />
        {reconnect ? 'Resolve in chat' : 'Set up with the agent in chat'}
      </Button>
    </div>
  );
}

/** What a member without the right to connect sees instead of the connect button. */
function NoRightsActions({ name }: { name: string }): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-xs text-muted-foreground">
        Only workspace owners and editors can connect {name}.
      </p>
      <CopyRequestButton text={adminRequest(name)} />
    </div>
  );
}

/** How the modal names a workspace owner: the directory name, else the email, else a generic phrase. */
function useOwnerName(workspaceId: string | null): (ownerId: string) => string {
  const members = useQuery({
    ...workspaceMembersQueryOptions(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
  });
  return (ownerId) => {
    const member = members.data?.find((entry) => entry.userId === ownerId);
    return member?.name || member?.email || 'a workspace owner';
  };
}

/**
 * Saves the caller's owner choice for one connector, or clears it with `null`, then reloads every
 * Quality read: a choice changes the account behind connector status, source discovery, publication
 * destinations and BrowserStack alike, so each must read again through the account now in use.
 */
function useConnectorChoice(
  projectId: string,
  provider: QualityConnectorId,
  onChoiceChange?: () => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ownerId: string | null) =>
      ownerId
        ? chooseConnectorOwner(projectId, provider, ownerId)
        : clearConnectorOwner(projectId, provider),
    onSuccess: () => {
      onChoiceChange?.();
      return queryClient.invalidateQueries({ queryKey: ['quality'] });
    },
    onError: (error) => notify.error(error.message),
  });
}

/** One button per workspace owner who lets members run Quality through their connection. */
function OwnerChoiceOffer({
  projectId,
  provider,
  workspaceId,
  ownerIds,
  onChoiceChange,
}: {
  projectId: string;
  provider: QualityConnectorId;
  workspaceId: string | null;
  ownerIds: string[];
  onChoiceChange?: () => void;
}): JSX.Element {
  const ownerName = useOwnerName(workspaceId);
  const choice = useConnectorChoice(projectId, provider, onChoiceChange);
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="text-xs text-muted-foreground">
        Or run Quality through a workspace owner&apos;s connection. Your runs
        then act as that owner.
      </p>
      <div className="flex flex-wrap gap-2">
        {ownerIds.map((ownerId) => (
          <Button
            key={ownerId}
            type="button"
            variant="outline"
            size="sm"
            className="text-xs"
            disabled={choice.isPending}
            onClick={() => choice.mutate(ownerId)}
          >
            {`Use ${ownerName(ownerId)}'s connection`}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Says whose connection Quality runs through for the caller, with the way back. */
function ChosenOwnerLine({
  projectId,
  provider,
  workspaceId,
  ownerId,
  onChoiceChange,
}: {
  projectId: string;
  provider: QualityConnectorId;
  workspaceId: string | null;
  ownerId: string;
  onChoiceChange?: () => void;
}): JSX.Element {
  const ownerName = useOwnerName(workspaceId);
  const choice = useConnectorChoice(projectId, provider, onChoiceChange);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-xs text-muted-foreground">
        {`Using ${ownerName(ownerId)}'s connection.`}
      </p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-xs"
        disabled={choice.isPending}
        onClick={() => choice.mutate(null)}
      >
        Stop using
      </Button>
    </div>
  );
}

/**
 * The modal a connector badge opens. A working connector shows where it comes from and where it is
 * managed; one that is missing or broken offers the directory's own connect flow, or, to a member
 * who may not connect, a request they can copy to an owner or editor.
 */
export function ConnectorModal({
  provider,
  status,
  projectId,
  workspaceId,
  open,
  onOpenChange,
  statusOverride,
  onChoiceChange,
  explainUnavailableActions = false,
}: {
  provider: QualityConnectorId;
  status: ConnectorBadgeStatus;
  /** The Quality project an owner choice is saved for; without it no choice is offered. */
  projectId?: string;
  explainUnavailableActions?: boolean;
  workspaceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statusOverride?: {
    state: 'connected' | 'disconnected' | 'unavailable';
    source: 'identity' | 'project' | null;
    reconnectConnectionId: null;
  };
  /** Called after an owner choice is saved or cleared, so a caller-held status can refresh. */
  onChoiceChange?: () => void;
}): JSX.Element {
  const name = QUALITY_CONNECTOR_NAMES[provider];
  const access = useConnectAccess(workspaceId, open);
  const effectiveStatus = statusOverride ?? status;
  const source = sourceLine({ ...status, source: effectiveStatus.source });
  const chosenOwnerId = projectId ? status.chosenOwnerId : null;
  const ownerOptions = projectId ? (status.ownerOptions ?? []) : [];
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={name}
      description={QUALITY_CONNECTOR_PURPOSE[provider]}
      titleHidden
      className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-3 p-6"
    >
      <div className="flex items-center gap-2">
        <ConnectorIcon id={provider} className="size-6 shrink-0" />
        <h3 className="text-base font-semibold">{name}</h3>
        <span
          data-testid="connector-modal-state"
          className={`rounded-full px-2 py-0.5 text-[11px] ${pillClass[effectiveStatus.state]}`}
        >
          {pillLabel[effectiveStatus.state]}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {QUALITY_CONNECTOR_PURPOSE[provider]}
      </p>
      {effectiveStatus.state === 'unavailable' && (
        <p role="status" className="text-xs text-muted-foreground">
          Quality could not confirm this connection. Check its setup, then
          return and refresh.
        </p>
      )}
      {effectiveStatus.state === 'connected' && projectId && chosenOwnerId ? (
        <ChosenOwnerLine
          projectId={projectId}
          provider={provider}
          workspaceId={workspaceId}
          ownerId={chosenOwnerId}
          onChoiceChange={onChoiceChange}
        />
      ) : effectiveStatus.state === 'connected' ? (
        <div className="flex flex-col items-start gap-2">
          {source ? (
            <p className="text-xs text-muted-foreground">{source}</p>
          ) : null}
          {workspaceId && effectiveStatus.source !== null ? (
            <ManageConnectionLink
              workspaceId={workspaceId}
              source={effectiveStatus.source}
            />
          ) : null}
        </div>
      ) : access === 'checking' ? (
        <p role="status" className="text-xs text-muted-foreground">
          Checking your access…
        </p>
      ) : access === 'allowed' ? (
        <ConnectActions
          explainUnavailableActions={explainUnavailableActions}
          provider={provider}
          reconnect={effectiveStatus.state === 'error'}
          reconnectConnectionId={effectiveStatus.reconnectConnectionId}
          workspaceId={workspaceId}
        />
      ) : (
        <NoRightsActions name={name} />
      )}
      {effectiveStatus.state !== 'connected' &&
      projectId &&
      ownerOptions.length > 0 ? (
        <OwnerChoiceOffer
          projectId={projectId}
          provider={provider}
          workspaceId={workspaceId}
          ownerIds={ownerOptions}
          onChoiceChange={onChoiceChange}
        />
      ) : null}
      <a
        href={connectorGuideUrl(provider)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        What Quality needs <ExternalLinkIcon className="size-3" aria-hidden />
      </a>
    </AppDialog>
  );
}
